import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  IconButton,
  Avatar,
  Badge,
  Tooltip,
  Grid,
  GridItem,
  Spinner,
  useToast,
  Input,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Image,
  Progress,
} from '@chakra-ui/react';
import {
  FiMic,
  FiMicOff,
  FiVideo,
  FiVideoOff,
  FiPhoneOff,
  FiMessageSquare,
  FiUsers,
  FiMaximize,
  FiMinimize,
  FiSend,
  FiCornerUpLeft,
  FiX,
  FiPaperclip,
  FiDownload,
  FiWifi,
  FiMonitor,
  FiDisc,
  FiGrid,
  FiLayout,
  FiStopCircle,
  FiAnchor,
} from 'react-icons/fi';
import io from 'socket.io-client';
import Cookies from 'js-cookie';
import {Device} from 'mediasoup-client';
import resumableUploader from '../../utils/resumableUpload';
import {processVideoForUpload} from '../../utils/videoOptimizer';

const getConferenceSocketUrl = () => {
  if (process.env.REACT_APP_CONFERENCE_URL) {
    return process.env.REACT_APP_CONFERENCE_URL.replace(/\/v1\/?$/, '');
  }
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/v1\/?$/, '');
  }
  return 'http://localhost:3000';
};

const SOCKET_URL = getConferenceSocketUrl();

const looksLikeIdentifier = value => {
  const text = String(value || '').trim();
  if (!text) return false;
  if (text.startsWith('sfu-')) return true;
  return /^[A-Za-z0-9_-]{12,}$/.test(text);
};

const isGenericParticipantName = value => {
  const text = String(value || '').trim().toLowerCase();
  return text === 'kullanıcı' || text === 'mobile user' || text === 'user' || text === 'participant';
};

const normalizeAlias = value => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.startsWith('sfu-') ? raw.slice(4) : raw;
};

const isMeaningfulDisplayName = value => {
  const text = String(value || '').trim();
  if (!text) return false;
  if (isGenericParticipantName(text)) return false;
  if (looksLikeIdentifier(text)) return false;
  return true;
};

const buildIncomingMessageId = data => {
  const existing = String(data?.id || '').trim();
  if (existing) return existing;
  const owner = String(data?.userId || data?.odaId || data?.socketId || 'anon');
  const ts = String(data?.timestamp || '0');
  const body = String(data?.content || data?.file?.name || data?.fileName || '').slice(0, 48);
  return `${owner}-${ts}-${body}`;
};

// ICE Servers for WebRTC (STUN + TURN)
// TURN servers are CRITICAL for users behind symmetric NAT (most mobile networks)
const DEFAULT_ICE_SERVERS = [
  // Google STUN servers (free, reliable)
  {urls: 'stun:stun.l.google.com:19302'},
  {urls: 'stun:stun1.l.google.com:19302'},
  // Own coturn server (production)
  {
    urls: 'turn:161.35.16.190:3478',
    username: 'paralaus',
    credential: 'TestPass123'
  },
  {
    urls: 'turn:161.35.16.190:3478?transport=tcp',
    username: 'paralaus',
    credential: 'TestPass123'
  },
  {
    urls: 'turns:161.35.16.190:5349',
    username: 'paralaus',
    credential: 'TestPass123'
  }
];
const getAiAudioConstraints = () => ({
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
  sampleRate: 48000,
  latency: 0.02,
  googEchoCancellation: true,
  googEchoCancellation2: true,
  googAutoGainControl: true,
  googNoiseSuppression: true,
  voiceIsolation: true,
});
const maybeEnhanceAudioStream = async (stream) => {
  const enabled = String(process.env.REACT_APP_AI_AUDIO_PROCESSING_ENABLED || 'true').toLowerCase() === 'true';
  if (!enabled || !stream) return stream;

  const enhancer = window?.__AI_AUDIO_ENHANCER__;
  if (enhancer?.enhanceStream) {
    try {
      const enhanced = await enhancer.enhanceStream(stream);
      if (enhanced) return enhanced;
    } catch (_e) {
      // fallback to built-in constraints
    }
  }

  try {
    const audioTrack = stream.getAudioTracks?.()[0];
    if (audioTrack?.applyConstraints) {
      await audioTrack.applyConstraints(getAiAudioConstraints());
    }
  } catch (_e) {
    // keep original stream
  }
  return stream;
};

// Adaptive bitrate configuration
const ADAPTIVE_BITRATE = {
  checkInterval: 3000,
  rttThresholds: {
    excellent: 50,
    good: 150,
    fair: 300,
  },
  bitrateProfiles: {
    excellent: {video: 1500000, audio: 64000},
    good: {video: 1000000, audio: 48000},
    fair: {video: 500000, audio: 32000},
    poor: {video: 200000, audio: 24000},
  },
};

// Ust katman HLS'e de giden katman (server en yuksek katmana kilitlenir);
// 720p30 icin 900 kbps yetersiz kaliyordu.
const createSimulcastEncodings = () => [
  {maxBitrate: 150000, scaleResolutionDownBy: 4},
  {maxBitrate: 500000, scaleResolutionDownBy: 2},
  {maxBitrate: 2500000, scaleResolutionDownBy: 1},
];

// Ekran paylasimi tek katman: dusuk cozunurluklu simulcast katmanlari
// metni okunmaz kilar, web istemcisi de tek katman kullanir.
const createScreenShareEncodings = () => [{maxBitrate: 3000000}];

const cleanupConferenceResources = ({
  adaptiveTimerRef,
  sfuProducersRef,
  sfuConsumersRef,
  sfuSendTransportRef,
  sfuRecvTransportRef,
  sfuDeviceRef,
  localStreamRef,
  peersRef,
  socketRef,
  initializedRef,
  screenStreamRef,
  recordingTimerRef,
}) => {
  if (adaptiveTimerRef.current) {
    clearInterval(adaptiveTimerRef.current);
    adaptiveTimerRef.current = null;
  }

  if (recordingTimerRef && recordingTimerRef.current) {
    clearInterval(recordingTimerRef.current);
    recordingTimerRef.current = null;
  }

  if (screenStreamRef && screenStreamRef.current) {
    screenStreamRef.current.getTracks().forEach(track => track.stop());
    screenStreamRef.current = null;
  }

  for (const producer of sfuProducersRef.current.values()) {
    producer.close();
  }
  sfuProducersRef.current.clear();

  for (const consumer of sfuConsumersRef.current.values()) {
    consumer.close();
  }
  sfuConsumersRef.current.clear();

  if (sfuSendTransportRef.current) {
    sfuSendTransportRef.current.close();
    sfuSendTransportRef.current = null;
  }
  if (sfuRecvTransportRef.current) {
    sfuRecvTransportRef.current.close();
    sfuRecvTransportRef.current = null;
  }
  sfuDeviceRef.current = null;

  if (localStreamRef.current) {
    localStreamRef.current.getTracks().forEach(track => track.stop());
    localStreamRef.current = null;
  }

  peersRef.current.forEach(pc => pc.close());
  peersRef.current.clear();

  if (socketRef.current) {
    socketRef.current.emit('leave-room');
    socketRef.current.disconnect();
    socketRef.current = null;
  }

  initializedRef.current = false;
};

// Video participant component
const VideoParticipant = ({
  participant,
  isLocal,
  isSpeaking,
  isFullscreen,
  onFullscreen,
  isSpotlighted,
  onSpotlight,
}) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && participant.stream) {
      console.log(
        `[VideoParticipant] Setting srcObject for ${participant.userName}, stream active: ${participant.stream.active}, tracks: ${participant.stream.getTracks().length}`,
      );
      participant.stream
        .getTracks()
        .forEach(t =>
          console.log(
            `[VideoParticipant] Track: ${t.kind}, enabled: ${t.enabled}, state: ${t.readyState}, id: ${t.id}`,
          ),
        );
      videoRef.current.srcObject = participant.stream;

      // Auto-play checks
      videoRef.current
        .play()
        .catch(e => {
          // Ignore AbortError which happens when component unmounts quickly
          if (e.name === 'AbortError') return;
          console.error('[VideoParticipant] Play error:', e);
        });
    }
  }, [participant.stream, participant.videoEnabled, participant.userName]);

  return (
    <Box
      position="relative"
      bg="gray.900"
      borderRadius="xl"
      overflow="hidden"
      border={isSpeaking ? '3px solid' : '1px solid'}
      borderColor={isSpeaking ? 'green.400' : 'gray.700'}
      transition="all 0.2s"
      minH="200px"
      h="100%">
      {participant.stream && participant.videoEnabled ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: isLocal ? 'scaleX(-1)' : 'none',
          }}
        />
      ) : (
        <Flex
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          justifyContent="center"
          alignItems="center"
          bg="gray.800">
          <Avatar
            size="2xl"
            name={participant.userName}
            src={participant.userAvatar}
          />
        </Flex>
      )}

      {/* Name badge */}
      <Box
        position="absolute"
        bottom="2"
        left="2"
        bg="blackAlpha.700"
        px="3"
        py="1"
        borderRadius="md">
        <HStack spacing="2">
          <Text color="white" fontSize="sm" fontWeight="500">
            {isLocal ? 'Sen' : participant.userName}
          </Text>
          {!participant.audioEnabled && <FiMicOff color="#EF4444" size={14} />}
          {participant.handRaised && <span style={{fontSize: '14px'}}>✋</span>}
        </HStack>
      </Box>

      {/* Fullscreen button */}
      <HStack
        position="absolute"
        top="2"
        right="2"
        spacing={1}
        opacity="0"
        _groupHover={{opacity: 1}}
        transition="opacity 0.2s">
        <IconButton
          icon={<FiAnchor />}
          size="sm"
          variant={isSpotlighted ? 'solid' : 'ghost'}
          colorScheme={isSpotlighted ? 'blue' : 'whiteAlpha'}
          onClick={() => onSpotlight?.(participant.odaId)}
          aria-label={isSpotlighted ? 'Sabitlemeyi Kaldır' : 'Sabitle'}
        />
        <IconButton
          icon={isFullscreen ? <FiMinimize /> : <FiMaximize />}
          size="sm"
          variant="ghost"
          colorScheme="whiteAlpha"
          onClick={() => onFullscreen?.(participant.odaId)}
          aria-label="Tam ekran"
        />
      </HStack>
    </Box>
  );
};

// Chat panel component with advanced features
const ChatPanel = ({
  messages,
  onSend,
  onSendFile,
  currentUserId,
  typingUsers = [],
  replyingTo,
  onReply,
  onCancelReply,
  onStartTyping,
  onStopTyping,
  isUploading = false,
  uploadProgress = 0,
}) => {
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = e => {
    const file = e.target.files?.[0];
    if (file) {
      onSendFile?.(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Format file size
  const formatFileSize = bytes => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Get file icon based on type
  const getFileIcon = type => {
    if (type?.startsWith('image/')) return '🖼️';
    if (type?.startsWith('video/')) return '🎥';
    if (type?.startsWith('audio/')) return '🎵';
    if (type?.includes('pdf')) return '📄';
    if (type?.includes('word') || type?.includes('document')) return '📝';
    if (type?.includes('excel') || type?.includes('spreadsheet')) return '📊';
    return '📎';
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
  }, [messages]);

  const handleTextChange = e => {
    const text = e.target.value;
    setMessage(text);

    // Typing indicator
    if (text.length > 0) {
      onStartTyping?.();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        onStopTyping?.();
      }, 2000);
    } else {
      onStopTyping?.();
    }
  };

  const handleSend = () => {
    if (message.trim()) {
      onSend(message, replyingTo);
      setMessage('');
      onStopTyping?.();
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Render clickable links in text
  const renderContent = content => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = content.split(urlRegex);
    return parts.map((part, i) => {
      if (urlRegex.test(part)) {
        return (
          <Text
            key={i}
            as="a"
            href={part}
            target="_blank"
            color="cyan.300"
            textDecoration="underline"
            _hover={{color: 'cyan.200'}}>
            {part}
          </Text>
        );
      }
      return part;
    });
  };

  return (
    <VStack h="100%" spacing="0">
      {/* Header */}
      <HStack
        w="100%"
        p="3"
        borderBottom="1px solid"
        borderColor="gray.700"
        justify="space-between">
        <Text color="white" fontWeight="bold" fontSize="sm">
          💬 Sohbet
        </Text>
        <Badge colorScheme="blue">{messages.length} mesaj</Badge>
      </HStack>

      {/* Messages */}
      <Box flex="1" w="100%" overflowY="auto" p="3">
        {messages.map((msg, idx) => {
          const isOwn = msg.odaId === currentUserId;
          return (
            <Box
              key={msg.id || idx}
              mb="3"
              ml={isOwn ? 'auto' : '0'}
              mr={isOwn ? '0' : 'auto'}
              maxW="85%"
              role="group">
              {/* Reply preview */}
              {msg.replyTo && (
                <Box
                  bg="gray.600"
                  p="2"
                  borderRadius="md"
                  borderLeft="3px solid"
                  borderLeftColor="blue.400"
                  mb="1"
                  fontSize="xs">
                  <Text color="blue.300" fontWeight="bold">
                    {msg.replyTo.userName}
                  </Text>
                  <Text color="gray.300" noOfLines={1}>
                    {msg.replyTo.content}
                  </Text>
                </Box>
              )}

              <Box
                p="3"
                bg={isOwn ? 'blue.500' : 'gray.700'}
                borderRadius="lg"
                borderBottomRightRadius={isOwn ? 'sm' : 'lg'}
                borderBottomLeftRadius={isOwn ? 'lg' : 'sm'}
                position="relative">
                {!isOwn && (
                  <Text fontSize="xs" color="blue.300" fontWeight="bold" mb="1">
                    {msg.userName}
                  </Text>
                )}

                {/* File message */}
                {msg.type === 'file' && msg.file ? (
                  <Box>
                    {/* Inline Media Preview */}
                    {msg.file.type?.startsWith('image/') && (
                      <Image
                        src={msg.file.url}
                        alt={msg.file.name}
                        maxH="200px"
                        borderRadius="md"
                        mb="2"
                        objectFit="cover"
                        cursor="pointer"
                        onClick={() => window.open(msg.file.url, '_blank')}
                      />
                    )}
                    {msg.file.type?.startsWith('video/') && (
                      <Box
                        as="video"
                        controls
                        src={msg.file.url}
                        maxH="300px"
                        w="100%"
                        borderRadius="md"
                        mb="2"
                        outline="none"
                      />
                    )}
                    
                    <HStack
                      p="2"
                      bg={isOwn ? 'blue.600' : 'gray.600'}
                      borderRadius="md"
                      spacing="3">
                      <Text fontSize="2xl">{getFileIcon(msg.file.type)}</Text>
                      <VStack align="start" spacing="0" flex="1" minW="0">
                        <Text
                          color="white"
                          fontSize="sm"
                          fontWeight="500"
                          noOfLines={1}>
                          {msg.file.name}
                        </Text>
                        <Text color="gray.300" fontSize="xs">
                          {formatFileSize(msg.file.size)}
                        </Text>
                      </VStack>
                      <IconButton
                        as="a"
                        href={msg.file.url}
                        target="_blank"
                        download={msg.file.name}
                        icon={<FiDownload />}
                        size="sm"
                        variant="ghost"
                        colorScheme="whiteAlpha"
                        aria-label="İndir"
                      />
                    </HStack>
                    {msg.content && (
                      <Text
                        color="white"
                        fontSize="sm"
                        mt="2"
                        whiteSpace="pre-wrap">
                        {renderContent(msg.content)}
                      </Text>
                    )}
                  </Box>
                ) : (
                  <Text color="white" fontSize="sm" whiteSpace="pre-wrap">
                    {renderContent(msg.content)}
                  </Text>
                )}
                <Text
                  fontSize="xs"
                  color={isOwn ? 'blue.100' : 'gray.400'}
                  textAlign="right"
                  mt="1">
                  {msg.timestamp
                    ? new Date(msg.timestamp).toLocaleTimeString('tr-TR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : ''}
                </Text>

                {/* Action buttons (visible on hover) */}
                <HStack
                  position="absolute"
                  top="-2"
                  right={isOwn ? 'auto' : '-2'}
                  left={isOwn ? '-2' : 'auto'}
                  opacity="0"
                  _groupHover={{opacity: 1}}
                  bg="gray.800"
                  borderRadius="md"
                  p="1"
                  spacing="1"
                  boxShadow="lg">
                  <Tooltip label="Cevapla">
                    <IconButton
                      icon={<FiCornerUpLeft />}
                      size="xs"
                      variant="ghost"
                      colorScheme="whiteAlpha"
                      onClick={() => onReply?.(msg)}
                      aria-label="Cevapla"
                    />
                  </Tooltip>
                </HStack>
              </Box>
            </Box>
          );
        })}
        <div ref={messagesEndRef} />
      </Box>

      {/* Typing indicator */}
      {typingUsers.length > 0 && (
        <Box px="3" py="1" w="100%">
          <Text fontSize="xs" color="gray.400" fontStyle="italic">
            {typingUsers.map(u => u.userName).join(', ')} yazıyor...
          </Text>
        </Box>
      )}

      {/* Reply preview */}
      {replyingTo && (
        <HStack
          w="100%"
          px="3"
          py="2"
          bg="gray.700"
          borderLeft="3px solid"
          borderLeftColor="blue.400"
          justify="space-between">
          <VStack align="start" spacing="0" flex="1">
            <Text fontSize="xs" color="blue.300" fontWeight="bold">
              {replyingTo.userName}'e cevap
            </Text>
            <Text fontSize="xs" color="gray.400" noOfLines={1}>
              {replyingTo.content}
            </Text>
          </VStack>
          <IconButton
            icon={<FiX />}
            size="xs"
            variant="ghost"
            colorScheme="whiteAlpha"
            onClick={onCancelReply}
            aria-label="İptal"
          />
        </HStack>
      )}

      {/* Upload Progress */}
      {isUploading && (
        <Box px="3" py="2" bg="gray.700" borderTop="1px solid" borderColor="gray.600">
          <HStack spacing="3">
            <Text fontSize="xs" color="blue.300" whiteSpace="nowrap">
              Yükleniyor... %{uploadProgress}
            </Text>
            <Progress
              value={uploadProgress}
              size="xs"
              colorScheme="blue"
              flex="1"
              borderRadius="full"
              hasStripe
              isAnimated
            />
          </HStack>
        </Box>
      )}

      {/* Input */}
      <HStack p="3" w="100%" borderTop={!isUploading ? "1px solid" : "none"} borderColor="gray.700">
        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          style={{display: 'none'}}
          accept="*/*"
        />

        {/* File upload button */}
        <Tooltip label="Dosya Ekle">
          <IconButton
            icon={isUploading ? <Spinner size="sm" /> : <FiPaperclip />}
            variant="ghost"
            colorScheme="whiteAlpha"
            onClick={() => fileInputRef.current?.click()}
            isDisabled={isUploading}
            aria-label="Dosya Ekle"
          />
        </Tooltip>

        <Input
          value={message}
          onChange={handleTextChange}
          onKeyPress={handleKeyPress}
          placeholder="Mesaj yaz..."
          bg="gray.700"
          border="none"
          color="white"
          _placeholder={{color: 'gray.400'}}
          flex="1"
        />
        <IconButton
          icon={<FiSend />}
          colorScheme="blue"
          onClick={handleSend}
          isDisabled={!message.trim()}
          aria-label="Gönder"
        />
      </HStack>
    </VStack>
  );
};

// Main VideoConference component
const VideoConference = ({roomId, channelId, title, onClose, isBroadcaster = false}) => {
  const toast = useToast();

  // State
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(true);
  const [localStream, setLocalStream] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'speaker', 'spotlight'
  const [spotlightUserId, setSpotlightUserId] = useState(null);
  const [activeSpeakerId, setActiveSpeakerId] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [messages, setMessages] = useState([]);
  const [handRaised, setHandRaised] = useState(false);
  const [fullscreenUser, setFullscreenUser] = useState(null);
  const [typingUsers, setTypingUsers] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [floatingReactions, setFloatingReactions] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  // const {upload, validateFile, isUploading, uploadProgress} = useFileInput();
  const [polls, setPolls] = useState([]);
  const [showCreatePoll, setShowCreatePoll] = useState(false);
  const [newPollQuestion, setNewPollQuestion] = useState('');
  const [newPollOptions, setNewPollOptions] = useState(['', '']);

  // Conference mode and network quality
  const [conferenceMode, setConferenceMode] = useState('mesh'); // 'mesh' or 'sfu'
  const conferenceModeRef = useRef('mesh'); // Ref to access mode inside callbacks without re-running effect
  const [networkQuality, setNetworkQuality] = useState('good'); // 'excellent', 'good', 'fair', 'poor'

  // Refs
  const socketRef = useRef(null);
  const peersRef = useRef(new Map());
  const participantsRef = useRef([]); // Ref to access participants inside callbacks
  
  // Keep participants ref in sync with state
  useEffect(() => {
    participantsRef.current = participants;
  }, [participants]);

  const iceCandidateQueueRef = useRef(new Map());
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const iceServersRef = useRef(DEFAULT_ICE_SERVERS);

  // SFU Mode Refs
  const sfuDeviceRef = useRef(null);
  const sfuSendTransportRef = useRef(null);
  const sfuRecvTransportRef = useRef(null);
  const sfuProducersRef = useRef(new Map());
  const sfuConsumersRef = useRef(new Map());

  // Adaptive bitrate refs
  const adaptiveTimerRef = useRef(null);
  const currentQualityRef = useRef('good');

  // Get current user with flexible parsing
  const getCurrentUser = () => {
    try {
      // 1. Try 'user' key (common)
      const userData = localStorage.getItem('user');
      if (userData) {
        const parsed = JSON.parse(userData);
        // Sometimes user is nested inside data or user property
        if (parsed.user) return parsed.user;
        if (parsed.data) return parsed.data;
        return parsed;
      }
      
      // 2. Try 'currentUser' key (sometimes used)
      const currentUserData = localStorage.getItem('currentUser');
      if (currentUserData) {
          return JSON.parse(currentUserData);
      }

      // 3. Try 'auth' key (if using redux-persist or similar)
      const authData = localStorage.getItem('auth');
      if (authData) {
          const parsed = JSON.parse(authData);
          if (parsed.user) return parsed.user;
      }

      // 4. Fallback: Decode token if available (JWT)
      const token = Cookies.get('token');
      if (token) {
          try {
              const base64Url = token.split('.')[1];
              const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
              const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
                  return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
              }).join(''));
              const decoded = JSON.parse(jsonPayload);
              return {
                  id: decoded.id || decoded.userId || decoded.sub,
                  name: decoded.name || decoded.userName || decoded.email || 'Admin User (Token)',
                  email: decoded.email,
                  // Add other fields from token
              };
          } catch (e) {
              console.warn('Failed to decode token for user info');
          }
      }

    } catch (e) {
      console.error('Error getting user:', e);
    }
    return null;
  };

  // Get auth token from cookies
  const getToken = () => {
    return Cookies.get('token');
  };

  const currentUser = getCurrentUser() || { 
      // Last resort fallback
      id: 'admin-fallback-' + Date.now(),
      name: 'Admin User (Fallback)'
  };

  // Create peer connection (or return existing one)
  const createPeerConnection = useCallback(
    (socketId, remoteUserId, remoteUserName, remoteUserAvatar) => {
      // Check if we already have a peer connection for this socket
      const existingPc = peersRef.current.get(socketId);
      if (existingPc) {
        console.log(`Reusing existing peer connection for ${socketId}`);
        return existingPc;
      }

      console.log(
        `Creating new peer connection for ${socketId} (${remoteUserName})`,
      );

      const pc = new RTCPeerConnection({
        iceServers: iceServersRef.current,
        iceCandidatePoolSize: 10,
      });

      // Add local tracks
      if (localStreamRef.current) {
        const tracks = localStreamRef.current.getTracks();
        console.log(`Adding ${tracks.length} local tracks to peer connection`);
        tracks.forEach(track => {
          pc.addTrack(track, localStreamRef.current);
        });
      } else {
        console.warn(
          'WARNING: No local stream available when creating peer connection',
        );
      }

      // ICE candidate handler
      pc.onicecandidate = event => {
        if (event.candidate && socketRef.current) {
          socketRef.current.emit('ice-candidate', {
            to: socketId,
            candidate: event.candidate,
          });
        }
      };

      // Track handler
      pc.ontrack = event => {
        console.log(
          `ontrack fired for ${remoteUserId}, streams: ${event.streams.length}`,
        );
        const remoteStream = event.streams[0];
        if (remoteStream) {
          const tracks = remoteStream.getTracks();
          console.log(`Remote stream received with ${tracks.length} tracks`);

          // Log each track's details
          tracks.forEach((track, index) => {
            console.log(
              `Track ${index}: kind=${track.kind}, enabled=${track.enabled}, muted=${track.muted}, readyState=${track.readyState}`,
            );
          });

          // Check if video track exists and is enabled
          const videoTracks = remoteStream.getVideoTracks();
          const audioTracks = remoteStream.getAudioTracks();
          console.log(
            `Video tracks: ${videoTracks.length}, Audio tracks: ${audioTracks.length}`,
          );

          if (videoTracks.length > 0) {
            const vt = videoTracks[0];
            console.log(
              `Video track state: enabled=${vt.enabled}, muted=${vt.muted}, readyState=${vt.readyState}`,
            );
          }

          setParticipants(prev => {
            const existing = prev.find(p => p.odaId === remoteUserId);
            if (existing) {
              console.log(
                `Updating existing participant ${remoteUserId} with stream`,
              );
              return prev.map(p =>
                p.odaId === remoteUserId ? {...p, stream: remoteStream} : p,
              );
            }
            console.log(`Adding new participant ${remoteUserId} with stream`);
            return [
              ...prev,
              {
                id: socketId,
                odaId: remoteUserId,
                userName: remoteUserName,
                userAvatar: remoteUserAvatar,
                stream: remoteStream,
                audioEnabled: true,
                videoEnabled: true,
                handRaised: false,
              },
            ];
          });
        } else {
          console.warn('WARNING: ontrack fired but no stream available');
        }
      };

      pc.onconnectionstatechange = () => {
        console.log(
          `Peer ${remoteUserId} connection state: ${pc.connectionState}`,
        );
        if (pc.connectionState === 'connected') {
          console.log(`Successfully connected to peer ${remoteUserId}`);
        } else if (pc.connectionState === 'failed') {
          console.error(`Peer connection failed for ${remoteUserId}`);
        }
      };

      // ICE connection state handler
      pc.oniceconnectionstatechange = () => {
        console.log(`ICE state for ${remoteUserId}: ${pc.iceConnectionState}`);
      };

      peersRef.current.set(socketId, pc);
      return pc;
    },
    [],
  );

  // Calculate quality level from network stats
  const calculateQualityLevel = (rtt, packetLoss) => {
    if (packetLoss > 10) return 'poor';
    if (packetLoss > 5) return 'fair';
    if (rtt < ADAPTIVE_BITRATE.rttThresholds.excellent) return 'excellent';
    if (rtt < ADAPTIVE_BITRATE.rttThresholds.good) return 'good';
    if (rtt < ADAPTIVE_BITRATE.rttThresholds.fair) return 'fair';
    return 'poor';
  };

  // Gather network statistics from peer connections
  const gatherNetworkStats = useCallback(async () => {
    const pcs = Array.from(peersRef.current.values());
    if (pcs.length === 0) return null;

    let totalRtt = 0;
    let totalPacketLoss = 0;
    let validStats = 0;

    for (const pc of pcs) {
      try {
        const stats = await pc.getStats();
        for (const report of stats.values()) {
          if (
            report.type === 'candidate-pair' &&
            report.state === 'succeeded'
          ) {
            if (report.currentRoundTripTime) {
              totalRtt += report.currentRoundTripTime * 1000;
              validStats++;
            }
          }
          if (report.type === 'inbound-rtp' && report.kind === 'video') {
            if (report.packetsLost !== undefined && report.packetsReceived) {
              const loss =
                (report.packetsLost /
                  (report.packetsReceived + report.packetsLost)) *
                100;
              totalPacketLoss += loss;
            }
          }
        }
      } catch (err) {
        console.warn('Error getting stats:', err);
      }
    }

    if (validStats === 0) return null;

    return {
      rtt: totalRtt / validStats,
      packetLoss: totalPacketLoss / pcs.length,
    };
  }, []);

  // Apply adaptive bitrate to all peer connections
  const applyAdaptiveBitrate = useCallback(async quality => {
    const profile = ADAPTIVE_BITRATE.bitrateProfiles[quality];
    const pcs = Array.from(peersRef.current.values());

    console.log(
      `Applying adaptive bitrate: ${quality} (video: ${profile.video / 1000}kbps)`,
    );

    for (const pc of pcs) {
      try {
        const senders = pc.getSenders();
        for (const sender of senders) {
          if (sender.track?.kind === 'video') {
            const params = sender.getParameters();
            if (!params.encodings) {
              params.encodings = [{}];
            }
            params.encodings[0].maxBitrate = profile.video;
            if (quality === 'poor') {
              params.encodings[0].scaleResolutionDownBy = 2;
            } else if (quality === 'fair') {
              params.encodings[0].scaleResolutionDownBy = 1.5;
            } else {
              params.encodings[0].scaleResolutionDownBy = 1;
            }
            await sender.setParameters(params);
          } else if (sender.track?.kind === 'audio') {
            const params = sender.getParameters();
            if (!params.encodings) {
              params.encodings = [{}];
            }
            params.encodings[0].maxBitrate = profile.audio;
            await sender.setParameters(params);
          }
        }
      } catch (err) {
        console.warn('Error applying adaptive bitrate:', err);
      }
    }
  }, []);

  // Start adaptive bitrate monitoring
  const startAdaptiveMonitoring = useCallback(() => {
    if (adaptiveTimerRef.current) {
      clearInterval(adaptiveTimerRef.current);
    }

    console.log('Starting adaptive bitrate monitoring');

    adaptiveTimerRef.current = setInterval(async () => {
      const stats = await gatherNetworkStats();
      if (!stats) return;

      const newQuality = calculateQualityLevel(stats.rtt, stats.packetLoss);

      if (newQuality !== currentQualityRef.current) {
        currentQualityRef.current = newQuality;
        setNetworkQuality(newQuality);
        await applyAdaptiveBitrate(newQuality);
        console.log(
          `Network quality changed to ${newQuality} (RTT: ${stats.rtt.toFixed(0)}ms, Loss: ${stats.packetLoss.toFixed(1)}%)`,
        );
      }
    }, ADAPTIVE_BITRATE.checkInterval);
  }, [gatherNetworkStats, applyAdaptiveBitrate]);

  // Stop adaptive bitrate monitoring
  const stopAdaptiveMonitoring = useCallback(() => {
    if (adaptiveTimerRef.current) {
      clearInterval(adaptiveTimerRef.current);
      adaptiveTimerRef.current = null;
      console.log('Stopped adaptive bitrate monitoring');
    }
  }, []);

  // Consume a producer (receive remote stream)
  const consumeProducer = useCallback(
    async (producerId, producerOdaId, kind) => {
      if (
        !socketRef.current ||
        !sfuDeviceRef.current ||
        !sfuRecvTransportRef.current
      ) {
        console.error('Cannot consume: missing dependencies');
        return;
      }

      try {
        console.log(`[SFU] Consuming ${kind} from producer ${producerOdaId} (ID: ${producerId})`);
        
        if (!producerOdaId) {
            console.error('[SFU] CRITICAL ERROR: producerOdaId is missing!');
        }

        const consumerData = await new Promise((resolve, reject) => {
          socketRef.current.emit(
            'consume',
            {
              producerId,
              consumerTransportId: sfuRecvTransportRef.current.id,
              rtpCapabilities: sfuDeviceRef.current.rtpCapabilities,
            },
            response => {
              if (response.error) reject(new Error(response.error));
              else resolve(response);
            },
          );
        });

        const consumer = await sfuRecvTransportRef.current.consume({
          id: consumerData.id,
          producerId: consumerData.producerId,
          kind: consumerData.kind,
          rtpParameters: consumerData.rtpParameters,
          appData: { producerOdaId }, // Attach producerOdaId to appData
        });

        // Also attach directly for easier access if needed
        consumer.appData = { ...consumer.appData, producerOdaId };
        consumer.producerOdaId = producerOdaId;

        sfuConsumersRef.current.set(consumer.id, consumer);

        // Create a MediaStream from the track
        let stream;
        
        setParticipants(prev => {
          const existing = prev.find(p => p.odaId === producerOdaId);
          const mediaKind = consumerData.kind || kind;
          
          if (existing && existing.stream) {
             console.log(`[SFU] Merging tracks for ${producerOdaId}. Existing tracks: ${existing.stream.getTracks().length}`);
             const existingTracks = existing.stream.getTracks();
             const mergedTracks = [
               ...existingTracks.filter(t => t.kind !== mediaKind),
               consumer.track,
             ];
             stream = new MediaStream(mergedTracks);
             
             return prev.map(p =>
               p.odaId === producerOdaId
                 ? {
                     ...p,
                     stream,
                     audioEnabled: mediaKind === 'audio' ? true : p.audioEnabled,
                     videoEnabled: mediaKind === 'video' ? true : p.videoEnabled,
                   }
                 : p,
             );
          } else {
             stream = new MediaStream([consumer.track]);
             console.log(`[SFU] Created new stream for ${producerOdaId}`);
             
             if (existing) {
               return prev.map(p =>
                 p.odaId === producerOdaId
                   ? {
                       ...p,
                       stream,
                       audioEnabled: mediaKind === 'audio' ? true : p.audioEnabled,
                       videoEnabled: mediaKind === 'video' ? true : p.videoEnabled,
                     }
                   : p,
               );
             }
             
             return [
                ...prev,
                {
                  id: `sfu-${producerOdaId}`,
                  odaId: producerOdaId,
                  userName: 'Kullanıcı', // Will be updated from user-joined/chat events
                  stream,
                  audioEnabled: mediaKind === 'audio',
                  videoEnabled: mediaKind === 'video',
                  handRaised: false,
                },
              ];
          }
        });

        console.log(
          `[SFU] Stream ready for ${producerOdaId}, tracks: ${stream ? stream.getTracks().length : 'N/A'}, kind: ${kind}`,
        );
        
        // Monitor track status
        const track = consumer.track;
        console.log(
          `[SFU] Track details: kind=${track.kind}, enabled=${track.enabled}, state=${track.readyState}, muted=${track.muted}, id=${track.id}`
        );
        
        track.onmute = () => {
          console.log(`[SFU] Track muted: ${track.id} (producer: ${producerOdaId})`);
        };
        
        track.onunmute = () => {
          console.log(`[SFU] Track unmuted: ${track.id} (producer: ${producerOdaId})`);
        };

        // Update participant with new stream
        // (State update already handled above with track merging)

        // Resume consumer
        console.log(`[SFU] Resuming consumer ${consumer.id} for ${producerOdaId}`);
        
        // Try multiple event names and parameter formats to ensure server compatibility
        const resumePayload = {
          consumerId: consumer.id,
          consumer_id: consumer.id,
        };
        
        socketRef.current.emit('sfu:resume-consumer', resumePayload);
        
        consumer.resume();

        console.log(`SFU Consuming ${kind} from ${producerOdaId} done`);
      } catch (err) {
        console.error(`Failed to consume ${kind} from ${producerOdaId}:`, err);
      }
    },
    [],
  );

  // Initialize SFU mode (Mediasoup)
  const initializeSfuMode = useCallback(
    async () => {
      if (!socketRef.current || !localStreamRef.current) {
        console.error('Cannot initialize SFU: missing socket or stream');
        return;
      }

      try {
        console.log('Initializing SFU mode...');

        // Get RTP capabilities from server
        const rtpCapabilities = await new Promise((resolve, reject) => {
          socketRef.current.emit('sfu:get-rtp-capabilities', response => {
            if (!response || response.error) reject(new Error(response?.error || 'No response from server'));
            else resolve(response.rtpCapabilities);
          });
        });

        // Load device
        const device = new Device();
        await device.load({routerRtpCapabilities: rtpCapabilities});
        sfuDeviceRef.current = device;
        console.log('SFU Device loaded');

        // Create send transport
        const sendTransportParams = await new Promise((resolve, reject) => {
          socketRef.current.emit('sfu:create-send-transport', response => {
            if (response.error) reject(new Error(response.error));
            else resolve(response.transport);
          });
        });

        const sendTransport = device.createSendTransport({
          id: sendTransportParams.id,
          iceParameters: sendTransportParams.iceParameters,
          iceCandidates: sendTransportParams.iceCandidates,
          dtlsParameters: sendTransportParams.dtlsParameters,
          iceServers: iceServersRef.current.length > 0 ? iceServersRef.current : DEFAULT_ICE_SERVERS,
        });

        // Add connection state logging for debugging
        sendTransport.on('connectionstatechange', (state) => {
          console.log(`[SFU] Send transport connection state: ${state}`);
          if (state === 'failed') {
            console.error('[SFU] Send transport connection FAILED - check firewall/TURN');
          }
        });
        sendTransport.on('icegatheringstatechange', (state) => {
          console.log(`[SFU] Send transport ICE gathering state: ${state}`);
        });

        sendTransport.on(
          'connect',
          async ({dtlsParameters}, callback, errback) => {
            try {
              socketRef.current.emit(
                'sfu:connect-transport',
                {
                  transportId: sendTransport.id,
                  dtlsParameters,
                },
                res => {
                  if (res.error) errback(new Error(res.error));
                  else callback();
                },
              );
            } catch (err) {
              errback(err);
            }
          },
        );

        sendTransport.on(
          'produce',
          async ({kind, rtpParameters, appData}, callback, errback) => {
            try {
              socketRef.current.emit(
                'sfu:produce',
                {
                  transportId: sendTransport.id,
                  kind,
                  rtpParameters,
                  appData,
                },
                res => {
                  if (res.error) errback(new Error(res.error));
                  else callback({id: res.id || res.producerId});
                },
              );
            } catch (err) {
              errback(err);
            }
          },
        );

        sfuSendTransportRef.current = sendTransport;
        console.log('SFU Send transport created');

        // Create recv transport
        const recvTransportParams = await new Promise((resolve, reject) => {
          socketRef.current.emit('sfu:create-recv-transport', response => {
            if (response.error) reject(new Error(response.error));
            else resolve(response.transport);
          });
        });

        const recvTransport = device.createRecvTransport({
          id: recvTransportParams.id,
          iceParameters: recvTransportParams.iceParameters,
          iceCandidates: recvTransportParams.iceCandidates,
          dtlsParameters: recvTransportParams.dtlsParameters,
          iceServers: iceServersRef.current.length > 0 ? iceServersRef.current : DEFAULT_ICE_SERVERS,
        });

        // Add connection state logging for debugging
        recvTransport.on('connectionstatechange', (state) => {
          console.log(`[SFU] Recv transport connection state: ${state}`);
          if (state === 'failed') {
            console.error('[SFU] Recv transport connection FAILED - check firewall/TURN');
          }
        });
        recvTransport.on('icegatheringstatechange', (state) => {
          console.log(`[SFU] Recv transport ICE gathering state: ${state}`);
        });

        recvTransport.on(
          'connect',
          async ({dtlsParameters}, callback, errback) => {
            try {
              socketRef.current.emit(
                'sfu:connect-transport',
                {
                  transportId: recvTransport.id,
                  dtlsParameters,
                },
                res => {
                  if (res.error) errback(new Error(res.error));
                  else callback();
                },
              );
            } catch (err) {
              errback(err);
            }
          },
        );

        sfuRecvTransportRef.current = recvTransport;
        console.log('SFU Recv transport created');

        // Produce local tracks
        const tracks = localStreamRef.current.getTracks();
        for (const track of tracks) {
          try {
            // Prefer H.264, then VP8. VP9 is deliberately excluded here (same as
            // the screen-share producer below): VP9 requires SVC
            // (scalabilityMode, single encoding) instead of classical simulcast,
            // and the live-HLS server pipeline's FFmpeg build cannot decode
            // VP9's multi-layer scalability structure over RTP at all ("VP9
            // scalability structure with multiple layers is not implemented") -
            // any admin-panel broadcast negotiating VP9 produces zero HLS
            // segments. H.264 also lets the server's live-HLS pipeline use a
            // cheap '-c:v copy' passthrough instead of transcoding.
            let codecOptions = {
                videoGoogleStartBitrate: 1000,
                opusStereo: 1,
                opusFec: 1,
                opusDtx: 1,
                opusMaxPlaybackRate: 48000,
            };

            let codecToUse = undefined;
            if (track.kind === 'video') {
                const codecs = device.rtpCapabilities.codecs;
                const h264Codec = codecs.find(c => c.mimeType.toLowerCase() === 'video/h264');
                const vp8Codec = codecs.find(c => c.mimeType.toLowerCase() === 'video/vp8');
                codecToUse = h264Codec || vp8Codec;
                if (codecToUse) {
                    console.log(`Using preferred video codec for producer: ${codecToUse.mimeType}`);
                }
            }

            const videoEncodings = track.kind === 'video' ? createSimulcastEncodings() : undefined;

            const producer = await sendTransport.produce({
              track,
              encodings: videoEncodings,
              codecOptions,
              codec: codecToUse, // Force specific codec
              appData: { 
                mediaType: track.kind,
                producerOdaId: currentUser?.id, // CRITICAL: Send User ID in appData for receiver mapping
              },
            });
            sfuProducersRef.current.set(producer.id, producer);
            console.log(`SFU Produced ${track.kind} track: ${producer.id}`);
          } catch (err) {
            console.error(`Failed to produce ${track.kind}:`, err);
          }
        }

        // Get existing producers and consume them
        console.log('SFU Fetching existing producers...');
        socketRef.current.emit('getProducers');

        console.log('SFU mode initialized successfully');
      } catch (err) {
        console.error('Failed to initialize SFU mode:', err);
        setConferenceMode('mesh');
      }
    },
    [currentUser?.id],
  );

  // Setup SFU event listeners
  const setupSfuEventListeners = useCallback(() => {
    if (!socketRef.current) return;
    socketRef.current.off('sfu:new-producer');
    socketRef.current.off('newProducers');
    socketRef.current.off('sfu:consumer-closed');
    socketRef.current.off('consumerClosed');

    socketRef.current.on('sfu:new-producer', async (data) => {
      console.log('SFU New producer received:', data);
      const { producerId, producerSocketId, kind, appData } = data;
      
      // Find the participant to get their OdaID
      // We might need to look up by socketId if odaId is not in appData
      let producerOdaId = appData?.producerOdaId;
      
      if (!producerOdaId) {
          // Try to find in participants list
          // Note: participants ref might be stale in callback, consider using a ref for participants or functional state update if possible,
          // but here we just need to call consumeProducer.
          // For now, let's rely on appData or passed params. 
          // If we can't find it, we might have an issue.
          // However, we can also pass producerSocketId and let consumeProducer resolve it or just pass it as is.
          // But consumeProducer expects producerOdaId for UI mapping.
          
          // Let's try to find it in the participants array from state (might be stale closure) or ref
          // We don't have participantsRef visible here in this scope, but we can assume it's accessible or use the state.
          // Actually, we do have participants state.
          
          // Better approach: server should probably include userId in the event, but for now let's try to get it.
          // If media-server doesn't send userId, we rely on socketId map.
      }
      
      // If we still don't have producerOdaId, use socketId as fallback or wait for user-joined
      if (!producerOdaId) {
         // Check if we can find it in participants state (closure)
         const p = participantsRef.current.find(p => p.id === producerSocketId);
         if (p) producerOdaId = p.odaId;
      }

      // Kendimiz ise işlemi durdur
      if (currentUser?.id && producerOdaId === currentUser.id) {
          console.log('Skipping consumption of own producer:', producerId);
          return;
      }

      await consumeProducer(
          producerId,
          producerOdaId || producerSocketId, // Fallback
          kind
      );
    });

    const handleConsumerClosed = data => {
      console.log('SFU Consumer closed:', data);
      const consumerId = data?.consumerId || data?.consumer_id;
      if (!consumerId || !sfuConsumersRef.current.has(consumerId)) return;
      const consumer = sfuConsumersRef.current.get(consumerId);
      const producerOdaId = consumer?.appData?.producerOdaId || consumer?.producerOdaId;
      const closedKind = data?.consumer_kind || data?.kind || consumer?.kind;
      try {
        consumer.close();
      } catch (_e) {
        // ignore
      }
      sfuConsumersRef.current.delete(consumerId);

      if (!producerOdaId || !closedKind) return;
      setParticipants(prev =>
        prev.map(p => {
          const matches =
            p.odaId === producerOdaId ||
            p.id === producerOdaId ||
            p.id === `sfu-${producerOdaId}`;
          if (!matches || !p.stream) return p;
          const nextTracks = p.stream.getTracks().filter(t => t.kind !== closedKind);
          const nextStream = nextTracks.length > 0 ? new MediaStream(nextTracks) : undefined;
          return {
            ...p,
            stream: nextStream,
            audioEnabled: closedKind === 'audio' ? false : p.audioEnabled,
            videoEnabled: closedKind === 'video' ? false : p.videoEnabled,
          };
        }),
      );
    };
    socketRef.current.on('sfu:consumer-closed', handleConsumerClosed);
    socketRef.current.on('consumerClosed', handleConsumerClosed);

    // Handle existing producers (array)
    socketRef.current.on('newProducers', async (producers) => {
      console.log('SFU Existing producers received:', producers.length);
      
      for (const p of producers) {
        const { producer_id, producer_socket_id, kind, appData } = p;
        let producerOdaId = appData?.producerOdaId;

        // Kendimiz ise atla
        if (currentUser?.id && producerOdaId === currentUser.id) {
            console.log('Skipping consumption of own producer (existing):', producer_id);
            continue;
        }

        if (!producerOdaId) {
             // Check if we can find it in participants state (closure)
             const participant = participantsRef.current.find(pt => pt.id === producer_socket_id);
             if (participant) producerOdaId = participant.odaId;
        }

        await consumeProducer(
            producer_id,
            producerOdaId || producer_socket_id, // Fallback
            kind
        );
      }
    });
  }, [consumeProducer, currentUser?.id]);

  // Update video priorities based on visibility and active speaker
  const updateVideoPriorities = useCallback((visibleParticipantIds, currentActiveSpeakerId) => {
    if (conferenceMode !== 'sfu' || !socketRef.current) return;

    sfuConsumersRef.current.forEach(consumer => {
      if (consumer.kind !== 'video') return;

      const producerOdaId = consumer.appData?.producerOdaId || consumer.producerOdaId; // Check appData first if stored there
      // In consumeProducer we set: id: `sfu-${producerOdaId}`... wait, where do we store producerOdaId on consumer?
      // In consumeProducer: const consumer = await sfuRecvTransportRef.current.consume(...)
      // We didn't attach producerOdaId to consumer object explicitly in the read code, but we might need to.
      // Let's check consumeProducer again.
      
      // We need to attach producerOdaId to consumer to identify it here.
      // I'll update consumeProducer as well or rely on a map.
      
      const isVisible = visibleParticipantIds.includes(producerOdaId);
      const isActiveSpeaker = producerOdaId === currentActiveSpeakerId;

      if (isVisible) {
        if (consumer.paused) {
          console.log(`[SFU] Resuming video consumer for ${producerOdaId}`);
          socketRef.current?.emit('sfu:resume-consumer', { consumerId: consumer.id });
          consumer.resume();
        }

        // Adjust quality layers
        // 0: low (thumbnail), 1: medium (grid), 2: high (speaker)
        const preferredLayer = isActiveSpeaker ? 2 : 1; 
        
        socketRef.current?.emit('sfu:consumer-set-layers', { 
          consumerId: consumer.id,
          spatialLayer: preferredLayer,
          temporalLayer: 2 
        });
      } else {
        if (!consumer.paused) {
          console.log(`[SFU] Pausing video consumer for ${producerOdaId}`);
          // socketRef.current?.emit('pauseConsumer', { consumerId: consumer.id });
          consumer.pause();
        }
      }
    });
  }, [conferenceMode]);

  // Effect to trigger video priority updates
  useEffect(() => {
    if (conferenceMode !== 'sfu') return;

    // Determine visible participants and active speaker
    const allParticipantIds = participants.map(p => p.odaId);
    let visibleIds = [];
    let effectiveActiveId = activeSpeakerId;

    if (viewMode === 'grid') {
      // In grid, everyone is visible
      visibleIds = allParticipantIds;
    } else {
      // In speaker/spotlight mode
      // Main user is spotlight user OR active speaker OR first remote user
      const mainUserId = spotlightUserId || activeSpeakerId || (participants.length > 0 ? participants[0].odaId : null);
      
      effectiveActiveId = mainUserId; // The main user gets high quality
      visibleIds = allParticipantIds; // We still show others in the strip, so they are visible
      // If we had pagination, we would filter here.
    }

    updateVideoPriorities(visibleIds, effectiveActiveId);

  }, [participants, viewMode, spotlightUserId, activeSpeakerId, conferenceMode, updateVideoPriorities]);


  // Cleanup SFU resources
  const cleanupSfu = useCallback(() => {
    for (const producer of sfuProducersRef.current.values()) {
      producer.close();
    }
    sfuProducersRef.current.clear();

    for (const consumer of sfuConsumersRef.current.values()) {
      consumer.close();
    }
    sfuConsumersRef.current.clear();

    if (sfuSendTransportRef.current) {
      sfuSendTransportRef.current.close();
      sfuSendTransportRef.current = null;
    }
    if (sfuRecvTransportRef.current) {
      sfuRecvTransportRef.current.close();
      sfuRecvTransportRef.current = null;
    }

    sfuDeviceRef.current = null;
    console.log('SFU resources cleaned up');
  }, []);

  // Spotlight
  const toggleSpotlight = useCallback((userId) => {
    if (spotlightUserId === userId) {
      setSpotlightUserId(null);
      // If we were in speaker mode and unspotlighted, we might want to stay in speaker mode
      // or go back to grid. Usually stay in speaker mode but dynamic.
      // Or if the user manually switched to grid, we respect that.
      // But if we clicked "Pin", we usually expect to see that user.
    } else {
      setSpotlightUserId(userId);
      setViewMode('speaker');
    }
  }, [spotlightUserId]);

  // Screen Share
  const stopScreenShare = useCallback(async () => {
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
      setIsScreenSharing(false);
      socketRef.current?.emit('screen-share-stop');

      if (conferenceMode === 'sfu') {
          const producer = sfuProducersRef.current.get('screen');
          if (producer) {
              producer.close();
              sfuProducersRef.current.delete('screen');
          }
      }
    }
  }, [conferenceMode]);

  const startScreenShare = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });

      screenStreamRef.current = stream;
      setIsScreenSharing(true);
      socketRef.current?.emit('screen-share-start');

      const videoTrack = stream.getVideoTracks()[0];
      // Bant daralinca cozunurluk yerine fps dussun; metin net kalsin.
      if ('contentHint' in videoTrack) videoTrack.contentHint = 'detail';

      videoTrack.onended = () => {
        stopScreenShare();
      };

      if (conferenceMode === 'sfu' && sfuSendTransportRef.current) {
        try {
          // VP9 yerine VP8/H264'a force ediyoruz (VP9 HLS pipeline'ini bozar).
          const screenCodecs = sfuDeviceRef.current?.rtpCapabilities?.codecs || [];
          const vp8Codec = screenCodecs.find(
            c => c.mimeType?.toLowerCase() === 'video/vp8',
          );
          const h264Codec = screenCodecs.find(
            c => c.mimeType?.toLowerCase() === 'video/h264',
          );
          const screenCodec = vp8Codec || h264Codec;

          const producer = await sfuSendTransportRef.current.produce({
            track: videoTrack,
            encodings: createScreenShareEncodings(),
            codec: screenCodec,
            appData: {
                source: 'screen',
                producerOdaId: currentUser?.id, // CRITICAL: Add User ID for mobile compatibility
            },
          });
          sfuProducersRef.current.set('screen', producer);
          
          producer.on('transportclose', () => {
             sfuProducersRef.current.delete('screen');
          });
          
          producer.on('trackended', () => {
             stopScreenShare();
          });
          
        } catch (err) {
          console.error('SFU Screen share produce error:', err);
        }
      } else {
        // Mesh mode: Add track to all peers
        peersRef.current.forEach(pc => {
             stream.getTracks().forEach(track => pc.addTrack(track, stream));
        });
      }
    } catch (err) {
      console.error('Error starting screen share:', err);
      toast({
        title: 'Ekran paylaşımı hatası',
        description: err.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [conferenceMode, toast, stopScreenShare, currentUser?.id]);

  // Recording
  const toggleRecording = useCallback(() => {
    if (!socketRef.current) return;

    if (isRecording) {
      socketRef.current.emit('stop-recording');
    } else {
      socketRef.current.emit('start-recording');
    }
  }, [isRecording]);

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Track if already initialized (for StrictMode)
  const initializedRef = useRef(false);

  // Initialize conference
  useEffect(() => {
    // Prevent double initialization in StrictMode
    if (initializedRef.current) {
      console.log('Already initialized, skipping...');
      return;
    }
    initializedRef.current = true;

    const initConference = async () => {
      try {
        // Get local stream with optimized settings
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: getAiAudioConstraints(),
          video: {
            width: {ideal: 1280, max: 1920},
            height: {ideal: 720, max: 1080},
            frameRate: {ideal: 30, max: 30},
            facingMode: 'user',
          },
        });
        const enhancedStream = await maybeEnhanceAudioStream(stream);

        setLocalStream(enhancedStream);
        localStreamRef.current = enhancedStream;

        // Connect to socket
        const token = getToken();
        if (!token) {
          toast({
            title: 'Oturum hatası',
            description: 'Lütfen tekrar giriş yapın',
            status: 'error',
          });
          onClose?.();
          return;
        }

        console.log('Connecting to:', SOCKET_URL);
        console.log('CurrentUser:', currentUser);
        const userName = currentUser?.fullname || currentUser?.name || currentUser?.userName || currentUser?.email || 'Admin User';
        console.log('Using UserName:', userName);
        console.log('Token:', token ? token.substring(0, 20) + '...' : 'null');

        // Use /conference namespace
        const socketUrl = `${SOCKET_URL}/conference`;

        socketRef.current = io(socketUrl, {
          path: '/conference-socket.io/', // Updated path
          auth: {token},
          query: {
            name: userName,
            avatar: currentUser?.avatar || '',
            // roomId LB consistent-hashing için handshake URL'ine eklenir
            // (nginx `hash $arg_roomId consistent;`). Aynı oda her zaman
            // aynı media-server node'una düşer.
            roomId: roomId || '',
          },
      transports: ['websocket'], // Force websocket for stability
      reconnection: true,
          reconnectionAttempts: 10,
          timeout: 20000,
          forceNew: true,
          rejectUnauthorized: false // Dev only: Accept self-signed certs if needed
        });

        // Recording events
        socketRef.current.on('recording-started', () => {
          setIsRecording(true);
          setRecordingDuration(0);
          recordingTimerRef.current = setInterval(() => {
            setRecordingDuration(prev => prev + 1);
          }, 1000);
          toast({ title: 'Kayıt başlatıldı', status: 'info' });
        });

        socketRef.current.on('recording-stopped', () => {
          setIsRecording(false);
          if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
          }
          toast({ title: 'Kayıt durduruldu', status: 'info' });
        });

        // Connection timeout
        const connectionTimeout = setTimeout(() => {
          if (!isConnected) {
            console.error('Connection timeout');
            toast({
              title: 'Bağlantı zaman aşımı',
              description: 'Sunucuya bağlanılamadı. Backend çalışıyor mu?',
              status: 'error',
            });
            setIsConnecting(false);
          }
        }, 15000);

        // Socket event handlers
        socketRef.current.on('connect', () => {
          console.log('Connected to conference server');
          clearTimeout(connectionTimeout);
          console.log('Joining room:', roomId);
          socketRef.current.emit('join-room', {roomId});
        });

        // Error from server
        socketRef.current.on('error', data => {
          console.error('Server error:', data);
          setIsConnecting(false);

          let errorMessage = data.message || 'Konferansa katılınamadı';
          if (data.message === 'Conference not found') {
            errorMessage =
              'Bu konferans artık aktif değil. Yeni bir konferans başlatın.';
          }

          toast({
            title: 'Konferans Hatası',
            description: errorMessage,
            status: 'warning',
            duration: 5000,
          });

          // Close the conference screen if conference not found
          if (data.message === 'Conference not found') {
            setTimeout(() => onClose?.(), 2000);
          }
        });

        socketRef.current.on('room-joined', async data => {
          console.log('Joined room:', data);
          iceServersRef.current = data.iceServers || DEFAULT_ICE_SERVERS;
          setIsConnected(true);
          setIsConnecting(false);

          // Set conference mode (mesh or sfu)
          // Force SFU mode for better stability across platforms
          const mode = 'sfu'; // data.mode || 'mesh';
          setConferenceMode(mode);
          conferenceModeRef.current = mode;
          console.log(`Conference mode: ${mode} (Forced SFU)`);

          // Start adaptive bitrate monitoring
          setTimeout(() => {
            startAdaptiveMonitoring();
          }, 2000);

          // Initialize participants state from room data
          if (data.participants && data.participants.length > 0) {
            console.log('Initializing participants from room data:', data.participants);
            setParticipants(prev => {
              const newParticipants = data.participants
                .filter(p => {
                    const pId = p.userId || p.id;
                    
                    // Filter out current user to avoid duplication
                    if (currentUser?.id && pId === currentUser.id) {
                        console.log('Skipping current user from participants list');
                        return false;
                    }

                    const exists = prev.some(existing => existing.odaId === pId);
                    if (exists) console.log(`Participant ${p.userName} (${pId}) already exists, skipping`);
                    return !exists;
                })
                .map(p => {
                  const pId = p.userId || p.id;
                  console.log(`Mapping participant: ${p.userName} -> odaId: ${pId}`);
                  return {
                    id: p.socketId,
                    odaId: pId,
                    userName: isMeaningfulDisplayName(String(p.userName || '')) ? p.userName : 'Kullanıcı',
                    userAvatar: p.userAvatar,
                    audioEnabled: true,
                    videoEnabled: true,
                    handRaised: false,
                  };
                });
              return [...prev, ...newParticipants];
            });
          }

          // Handle connection based on mode
          if (mode === 'sfu') {
            // SFU Mode: Initialize mediasoup client
            await initializeSfuMode();
            setupSfuEventListeners();
          } else {
            // Mesh Mode: Create peer connections
            for (const participant of data.participants) {
              console.log('Found existing participant:', participant.userName);

              const pc = createPeerConnection(
                participant.socketId,
                participant.userId,
                participant.userName,
                participant.userAvatar,
              );

              try {
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);

                socketRef.current.emit('offer', {
                  to: participant.socketId,
                  offer,
                  userId: currentUser?.id,
                  userName: currentUser?.name,
                  userAvatar: currentUser?.thumbnail,
                });
                console.log(
                  'Sent offer to existing participant:',
                  participant.userName,
                );
              } catch (err) {
                console.error(
                  'Error sending offer to existing participant:',
                  err,
                );
              }

              setParticipants(prev => {
                if (prev.some(p => p.odaId === participant.userId)) return prev;
                return [
                  ...prev,
                  {
                    id: participant.socketId,
                    odaId: participant.userId,
                    userName: participant.userName,
                    userAvatar: participant.userAvatar,
                    audioEnabled: true,
                    videoEnabled: true,
                    handRaised: false,
                  },
                ];
              });
            }
          }
        });

        // Handle mode switch (mesh to sfu)
        socketRef.current.on('mode-switch', async data => {
          console.log('Mode switch requested:', data.mode);
          if (data.mode === 'sfu' && conferenceModeRef.current !== 'sfu') {
            setConferenceMode('sfu');
            conferenceModeRef.current = 'sfu';

            // Close all P2P connections
            peersRef.current.forEach(pc => pc.close());
            peersRef.current.clear();

            // Initialize SFU mode
            await initializeSfuMode();
            setupSfuEventListeners();
          }
        });

        socketRef.current.on('dominantSpeaker', ({peer_id}) => {
          setActiveSpeakerId(peer_id);
        });

        // Mobile compatibility handlers
        socketRef.current.on('dominant-speaker', ({producerOdaId}) => {
          setActiveSpeakerId(producerOdaId);
        });

        socketRef.current.on('participants-updated', data => {
          console.log('Participants updated:', data);
          const participantList = Array.isArray(data) ? data : data.participants || [];
          
          setParticipants(prev => {
            // Filter out current user from the incoming list
            const filteredList = participantList.filter(p => {
                const pId = p.userId || p.odaId || p.id;
                return !currentUser?.id || pId !== currentUser.id;
            });

            // Merge existing stream/track data with new participant info
            return filteredList.map(newP => {
              const targetId = newP.userId || newP.odaId || newP.id;
              const existing = prev.find(p => p.odaId === targetId);
              return {
                ...newP,
                odaId: targetId, // Ensure odaId is set
                stream: existing?.stream, // Keep existing stream
                audioEnabled: existing ? existing.audioEnabled : newP.audioEnabled,
                videoEnabled: existing ? existing.videoEnabled : newP.videoEnabled,
                handRaised: newP.handRaised,
              };
            });
          });
        });

        socketRef.current.on('new-poll', data => {
          console.log('New poll received:', data);
          setPolls(prev => {
             if (prev.some(p => p.id === data.id)) return prev;
             return [...prev, data];
          });
          toast({
            title: '📊 Yeni Anket',
            description: data.question,
            status: 'info',
            duration: 3000,
          });
        });

        socketRef.current.on('user-joined', async data => {
          console.log('User joined event received:', data);
          
          // We don't create an offer here to avoid signaling glare.
          // The new user (who just joined and received room-joined) will initiate the connection.
          // We just update the UI to show the new user.

          setParticipants(prev => {
            const targetId = data.userId || data.id;
            
            // Filter out current user
            if (currentUser?.id && targetId === currentUser.id) {
                return prev;
            }

            console.log('Processing user-joined. Target ID:', targetId, 'Current count:', prev.length);
            
            // Check if user already exists (by odaId OR by SFU temporary ID)
            const existingIndex = prev.findIndex(p => 
                p.odaId === targetId || 
                p.id === `sfu-${targetId}`
            );

            if (existingIndex !== -1) {
              const existing = prev[existingIndex];
              console.log('User already in participants list, updating:', existing.userName);
              
              // Update existing participant (merge new info with existing stream/state)
              const newParticipants = [...prev];
              newParticipants[existingIndex] = {
                  ...existing,
                  id: data.socketId, // Update with real socket ID
                  odaId: targetId,
                  userName: isMeaningfulDisplayName(String(data.userName || '')) ? data.userName : (existing.userName || 'Kullanıcı'),
                  userAvatar: data.userAvatar,
              };
              return newParticipants;
            }
            
            // Check if we have this user by socketId but missing odaId (edge case)
            const existingBySocket = prev.find(p => p.id === data.socketId);
            if (existingBySocket) {
               console.log('User found by socketId but missing odaId, updating:', existingBySocket);
               return prev.map(p =>
                 p.id === data.socketId
                   ? {
                       ...p,
                       odaId: targetId,
                       userName: isMeaningfulDisplayName(String(data.userName || '')) ? data.userName : (p.userName || 'Kullanıcı'),
                     }
                   : p,
               );
            }

            const newP = {
              id: data.socketId,
              odaId: targetId,
              userName: isMeaningfulDisplayName(String(data.userName || '')) ? data.userName : 'Kullanıcı',
              userAvatar: data.userAvatar,
              audioEnabled: true,
              videoEnabled: true,
              handRaised: false,
            };
            console.log('Adding new participant:', newP);
            return [...prev, newP];
          });

          toast({
            title: `${data.userName} katıldı`,
            status: 'info',
            duration: 2000,
          });
        });

        socketRef.current.on('offer', async data => {
          if (conferenceModeRef.current === 'sfu') return;
          try {
            let pc = peersRef.current.get(data.from);

            // If we don't have a peer connection, create one
            if (!pc) {
              pc = createPeerConnection(
                data.from,
                data.userId,
                data.userName || '',
                data.userAvatar || '',
              );
            }

            // Handle glare - if we're in have-local-offer state, rollback and accept the incoming offer
            // "Polite peer" pattern - we're polite, so we rollback our offer
            if (pc.signalingState === 'have-local-offer') {
              console.log('Glare detected, rolling back local offer');
              await pc.setLocalDescription({type: 'rollback'});
            }

            // Now we should be in stable state
            if (pc.signalingState === 'stable') {
              await pc.setRemoteDescription(
                new RTCSessionDescription(data.offer),
              );

              // Process queued ICE candidates
              const queue = iceCandidateQueueRef.current.get(data.from);
              if (queue && queue.length > 0) {
                console.log(
                  `Processing ${queue.length} queued ICE candidates for ${data.from}`,
                );
                for (const candidate of queue) {
                  await pc.addIceCandidate(new RTCIceCandidate(candidate));
                }
                iceCandidateQueueRef.current.delete(data.from);
              }

              const answer = await pc.createAnswer();
              await pc.setLocalDescription(answer);

              socketRef.current.emit('answer', {
                to: data.from,
                answer,
              });
            } else {
              console.log(
                'Cannot process offer, signaling state:',
                pc.signalingState,
              );
            }
          } catch (err) {
            console.error('Error handling offer:', err);
          }
        });

        socketRef.current.on('answer', async data => {
          if (conferenceModeRef.current === 'sfu') return;
          try {
            const pc = peersRef.current.get(data.from);
            if (pc) {
              // Only set remote description if we're expecting an answer
              if (pc.signalingState === 'have-local-offer') {
                await pc.setRemoteDescription(
                  new RTCSessionDescription(data.answer),
                );

                // Process queued ICE candidates
                const queue = iceCandidateQueueRef.current.get(data.from);
                if (queue && queue.length > 0) {
                  console.log(
                    `Processing ${queue.length} queued ICE candidates for ${data.from}`,
                  );
                  for (const candidate of queue) {
                    await pc.addIceCandidate(new RTCIceCandidate(candidate));
                  }
                  iceCandidateQueueRef.current.delete(data.from);
                }
              } else {
                console.log(
                  'Unexpected answer, signaling state:',
                  pc.signalingState,
                );
              }
            }
          } catch (err) {
            console.error('Error handling answer:', err);
          }
        });

        socketRef.current.on('ice-candidate', async data => {
          if (conferenceModeRef.current === 'sfu') return;
          try {
            const pc = peersRef.current.get(data.from);
            if (pc) {
              if (pc.remoteDescription) {
                await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
              } else {
                // Queue candidate if remote description is not set yet
                console.log(
                  `Queuing ICE candidate for ${data.from} (no remote description)`,
                );
                const queue = iceCandidateQueueRef.current.get(data.from) || [];
                queue.push(data.candidate);
                iceCandidateQueueRef.current.set(data.from, queue);
              }
            }
          } catch (err) {
            console.error('Error adding ICE candidate:', err);
          }
        });

        socketRef.current.on('user-left', data => {
          console.log('User left:', data);
          peersRef.current.get(data.socketId)?.close();
          peersRef.current.delete(data.socketId);
          const leftSocketId = normalizeAlias(data.socketId);
          const leftUserId = normalizeAlias(data.userId || data.odaId || data.id);
          setParticipants(prev =>
            prev.filter(p => {
              const pid = normalizeAlias(p.id);
              const poda = normalizeAlias(p.odaId);
              if (leftSocketId && (pid === leftSocketId || poda === leftSocketId)) return false;
              if (leftUserId && (pid === leftUserId || poda === leftUserId)) return false;
              return true;
            }),
          );

          toast({
            title: `${data.userName || 'Kullanıcı'} ayrıldı`,
            status: 'info',
            duration: 2000,
          });
        });

        // Media controls
        socketRef.current.on('user-audio-toggle', data => {
          console.log('Audio toggle received:', data);
          setParticipants(prev =>
            prev.map(p =>
              p.odaId === data.userId ? {...p, audioEnabled: data.enabled} : p,
            ),
          );
        });

        socketRef.current.on('user-video-toggle', data => {
          console.log('Video toggle received:', data);
          setParticipants(prev =>
            prev.map(p =>
              p.odaId === data.userId ? {...p, videoEnabled: data.enabled} : p,
            ),
          );
        });

        // Hand raise
        socketRef.current.on('hand-raise-update', data => {
          console.log('hand-raise-update received:', data);
          setParticipants(prev => {
            console.log(
              'Current participants for hand-raise:',
              JSON.stringify(prev.map(p => ({odaId: p.odaId, userName: p.userName, id: p.id, userId: p.userId}))),
            );
            
            // Search by multiple IDs to be robust
            const targetId = data.userId || data.id;
            const found = prev.find(p => p.odaId === targetId || p.id === targetId || p.odaId === data.userId || p.userId === targetId);
            
            console.log(
              'Found participant to update:',
              found ? (found.userName || 'Unknown') : 'NOT FOUND',
              'Searching for:', targetId, data.userId
            );

            if (!found) return prev;

            return prev.map(p =>
              (p.odaId === targetId || p.id === targetId || p.odaId === data.userId || p.userId === targetId) 
                ? {...p, handRaised: data.raised} 
                : p,
            );
          });
        });

        // User reactions (floating emojis)
        socketRef.current.on('user-reaction', data => {
          const reactionAlias = normalizeAlias(data.userId || data.odaId || data.socketId);
          const participantName = participantsRef.current.find(p => {
            const pid = normalizeAlias(p.id);
            const poda = normalizeAlias(p.odaId);
            return reactionAlias && (pid === reactionAlias || poda === reactionAlias);
          })?.userName;
          const resolvedReactionName = isMeaningfulDisplayName(String(data.userName || ''))
            ? String(data.userName)
            : (isMeaningfulDisplayName(String(participantName || '')) ? String(participantName) : 'Kullanıcı');
          const reactionId = Date.now() + Math.random();
          setFloatingReactions(prev => [
            ...prev,
            {
              id: reactionId,
              emoji: data.emoji,
              userName: resolvedReactionName,
            },
          ]);
          // Remove after animation
          setTimeout(() => {
            setFloatingReactions(prev => prev.filter(r => r.id !== reactionId));
          }, 3000);
        });

        // Poll events
        socketRef.current.on('poll-created', data => {
          console.log('Poll created:', data);
          setPolls(prev => {
            if (prev.some(p => p.id === data.poll.id)) return prev;
            return [...prev, data.poll];
          });
          toast({
            title: '📊 Yeni Anket',
            description: data.poll.question,
            status: 'info',
            duration: 3000,
          });
        });

        socketRef.current.on('poll-vote', data => {
          setPolls(prev =>
            prev.map(poll => {
              if (poll.id !== data.pollId) return poll;
              const existingVote = poll.votes?.find(
                v => v.odaId === data.odaId,
              );
              if (existingVote && !poll.allowMultiple) return poll;
              const newVotes = [
                ...(poll.votes || []),
                {odaId: data.odaId, optionIndex: data.optionIndex},
              ];
              const newOptions = poll.options.map((opt, idx) =>
                idx === data.optionIndex
                  ? {...opt, voteCount: (opt.voteCount || 0) + 1}
                  : opt,
              );
              return {
                ...poll,
                votes: newVotes,
                options: newOptions,
                totalVotes: newVotes.length,
              };
            }),
          );
        });

        socketRef.current.on('poll-closed', data => {
          setPolls(prev =>
            prev.map(poll =>
              poll.id === data.pollId ? {...poll, isActive: false} : poll,
            ),
          );
        });

        // Chat messages
        socketRef.current.on('chat-message', data => {
          const incomingId = buildIncomingMessageId(data);
          setMessages(prev => {
            if (prev.some(msg => msg.id === incomingId)) return prev;
            return [...prev, {...data, id: incomingId}];
          });
        });

        // Typing indicators
        socketRef.current.on('user-typing', data => {
          setTypingUsers(prev => {
            if (!prev.find(u => u.odaId === data.userId)) {
              return [...prev, {odaId: data.userId, userName: data.userName}];
            }
            return prev;
          });
        });

        socketRef.current.on('user-stop-typing', data => {
          setTypingUsers(prev => prev.filter(u => u.odaId !== data.userId));
        });

        // Message reactions
        socketRef.current.on('message-reaction', data => {
          const reactionUserId = String(data.userId || data.odaId || data.socketId || '');
          setMessages(prev =>
            prev.map(msg => {
              if (msg.id === data.messageId) {
                const reactions = {...(msg.reactions || {})};
                const emoji = data.emoji;
                const userId = reactionUserId;
                if (!reactions[emoji]) reactions[emoji] = [];
                if (!reactions[emoji].includes(userId)) {
                  reactions[emoji].push(userId);
                }
                return {...msg, reactions};
              }
              return msg;
            }),
          );
        });

        socketRef.current.on('connect_error', err => {
          console.error('Socket connection error:', err.message, err);
          setIsConnecting(false);
          toast({
            title: 'Bağlantı hatası',
            description: `Konferansa bağlanılamadı: ${err.message}`,
            status: 'error',
            duration: 10000,
          });
        });

        socketRef.current.on('disconnect', () => {
          console.log('Disconnected from conference');
          setIsConnected(false);
        });
      } catch (err) {
        console.error('Conference init error:', err);
        setIsConnecting(false);

        if (err.name === 'NotAllowedError') {
          toast({
            title: 'İzin gerekli',
            description: 'Kamera ve mikrofon izni verin',
            status: 'error',
          });
        } else {
          toast({
            title: 'Hata',
            description: 'Konferans başlatılamadı',
            status: 'error',
          });
        }
      }
    };

    initConference();

    return () => {
      console.log('Conference cleanup called');
      cleanupConferenceResources({
        adaptiveTimerRef,
        sfuProducersRef,
        sfuConsumersRef,
        sfuSendTransportRef,
        sfuRecvTransportRef,
        sfuDeviceRef,
        localStreamRef,
        peersRef,
        socketRef,
        initializedRef,
        screenStreamRef,
        recordingTimerRef,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    roomId,
    createPeerConnection,
    startAdaptiveMonitoring,
    initializeSfuMode,
    setupSfuEventListeners,
    currentUser?.id,
    currentUser?.name,
    currentUser?.thumbnail,
  ]);

  // Toggle audio
  const toggleAudio = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setAudioEnabled(audioTrack.enabled);
        socketRef.current?.emit('toggle-audio', {enabled: audioTrack.enabled});
      }
    }
  };

  // Toggle video
  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        // Track'i durdurma, sadece enabled/disabled yap
        const newState = !videoTrack.enabled;
        videoTrack.enabled = newState;
        setVideoEnabled(newState);
        socketRef.current?.emit('toggle-video', {enabled: newState});
      }
    }
  };

  // Toggle hand raise
  const toggleHandRaise = () => {
    const newState = !handRaised;
    setHandRaised(newState);
    socketRef.current?.emit('hand-raise', {raised: newState});
  };

  // Poll functions
  const createPoll = () => {
    const validOptions = newPollOptions.filter(o => o.trim());
    if (!newPollQuestion.trim() || validOptions.length < 2) {
      toast({
        title: 'Soru ve en az 2 seçenek gerekli',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    const pollId = Date.now().toString();
    const poll = {
      id: pollId,
      question: newPollQuestion.trim(),
      options: validOptions.map(text => ({text: text.trim(), voteCount: 0})),
      isActive: true,
      isAnonymous: false,
      allowMultiple: false,
      showResults: true,
      createdBy: {
        odaId: currentUser?.id,
        name: currentUser?.name,
      },
      votes: [],
      totalVotes: 0,
    };

    // Optimistic update - Add to local list immediately
    setPolls(prev => [...prev, poll]);

    socketRef.current?.emit('poll-created', {poll});
    setNewPollQuestion('');
    setNewPollOptions(['', '']);
    setShowCreatePoll(false);

    toast({
      title: 'Anket oluşturuldu',
      status: 'success',
      duration: 2000,
    });
  };

  const votePoll = (pollId, optionIndex) => {
    // Optimistic update
    setPolls(prev =>
      prev.map(poll => {
        if (poll.id !== pollId) return poll;

        const hasVoted = poll.votes?.some(v => v.odaId === currentUser?.id);
        if (hasVoted && !poll.allowMultiple) return poll;

        const newVotes = [
          ...(poll.votes || []),
          {odaId: currentUser?.id, optionIndex},
        ];
        const newOptions = poll.options.map((opt, idx) =>
          idx === optionIndex
            ? {...opt, voteCount: (opt.voteCount || 0) + 1}
            : opt,
        );
        return {
          ...poll,
          votes: newVotes,
          options: newOptions,
          totalVotes: newVotes.length,
        };
      }),
    );

    socketRef.current?.emit('poll-vote', {pollId, optionIndex});
    
    toast({
      title: 'Oyunuz kaydedildi',
      status: 'success',
      duration: 2000,
    });
  };

  const closePoll = pollId => {
    socketRef.current?.emit('poll-closed', {pollId});
  };

  // Send chat message
  const sendMessage = (content, replyTo = null) => {
    const messageId = `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const messageData = {id: messageId, content, type: 'text'};
    if (replyTo) {
      messageData.replyTo = {
        id: replyTo.id,
        userName: replyTo.userName,
        content: replyTo.content,
      };
    }
    socketRef.current?.emit('chat-message', messageData);
    setReplyingTo(null);
  };

  // Send file message
  const sendFile = async file => {
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);
    try {
      // Process video
      const processResult = await processVideoForUpload(file, {
        onProgress: (stage, progress) => {
          console.log(`Processing stage: ${stage}, progress: ${progress}%`);
        },
      });

      if (!processResult.success) {
        throw new Error(processResult.error || 'Video işleme başarısız');
      }

      // Upload file using resumable upload
      await resumableUploader.upload({
        file: processResult.processedFile,
        onProgress: progress => {
          setUploadProgress(progress.percentage);
        },
        onComplete: url => {
          // Send file message via socket
          socketRef.current?.emit('chat-message', {
            id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            type: 'file',
            file: {
              name: file.name,
              url: url,
              type: file.type,
              size: file.size,
              thumbnail: processResult.thumbnail,
            },
          });

          toast({
            title: 'Dosya gönderildi',
            status: 'success',
            duration: 2000,
          });
        },
        onError: error => {
          throw error;
        },
      });
    } catch (err) {
      console.error('File upload error:', err);
      toast({
        title: 'Dosya gönderilemedi',
        description: err.message || 'Bir hata oluştu',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Typing indicators
  const startTyping = () => {
    socketRef.current?.emit('typing-start');
  };

  const stopTyping = () => {
    socketRef.current?.emit('typing-stop');
  };

  // Send floating reaction (visible to all participants)
  const sendReaction = emoji => {
    socketRef.current?.emit('reaction', {emoji});
    setShowReactionPicker(false);
  };

  // Leave conference
  const leaveConference = () => {
    // Stop adaptive monitoring
    stopAdaptiveMonitoring();

    // Cleanup SFU if in SFU mode
    if (conferenceMode === 'sfu') {
      cleanupSfu();
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    peersRef.current.forEach(pc => pc.close());
    if (socketRef.current) {
      socketRef.current.emit('leave-room');
      socketRef.current.disconnect();
    }
    onClose?.();
  };

  const localDisplayName = currentUser?.fullname || currentUser?.name || currentUser?.userName || currentUser?.email || 'Ben';

  const normalizedRemoteParticipants = useMemo(() => {
    const merged = [];

    for (const participant of participants) {
      const pid = String(participant?.id || '');
      const poda = String(participant?.odaId || '');

      if (
        (currentUser?.id && (poda === currentUser.id || pid === currentUser.id || pid === `sfu-${currentUser.id}`))
      ) {
        continue;
      }

      const existingIndex = merged.findIndex(existing => {
        const eid = String(existing?.id || '');
        const eoda = String(existing?.odaId || '');
        return (
          (pid && (pid === eid || pid === eoda)) ||
          (poda && (poda === eid || poda === eoda))
        );
      });

      if (existingIndex === -1) {
        merged.push(participant);
        continue;
      }

      const existing = merged[existingIndex];
      const a = String(existing?.userName || '');
      const b = String(participant?.userName || '');
      const aWeak = !a || isGenericParticipantName(a) || looksLikeIdentifier(a);
      const bWeak = !b || isGenericParticipantName(b) || looksLikeIdentifier(b);
      const preferredName = aWeak && !bWeak ? b : bWeak && !aWeak ? a : (aWeak && bWeak ? 'Kullanıcı' : (a.length >= b.length ? a : b));

      merged[existingIndex] = {
        ...existing,
        ...participant,
        id: existing.id || participant.id,
        odaId: existing.odaId || participant.odaId,
        userName: preferredName || existing.userName || participant.userName || 'Katılımcı',
        userAvatar: existing.userAvatar || participant.userAvatar,
        stream: existing.stream || participant.stream,
        audioEnabled: Boolean(existing.audioEnabled || participant.audioEnabled),
        videoEnabled: Boolean(existing.videoEnabled || participant.videoEnabled),
        handRaised: Boolean(existing.handRaised || participant.handRaised),
        isScreenSharing: Boolean(existing.isScreenSharing || participant.isScreenSharing),
      };
    }

    return merged.map(item => ({
      ...item,
      userName: isMeaningfulDisplayName(String(item?.userName || ''))
        ? String(item.userName)
        : 'Kullanıcı',
    }));
  }, [participants, currentUser?.id]);

  // Local participant for display
  const localParticipant = {
    odaId: currentUser?.id,
    userName: localDisplayName,
    userAvatar: currentUser?.thumbnail,
    stream: localStream,
    audioEnabled,
    videoEnabled,
    handRaised,
  };

  // Filter out any participants that match the local user's ID to prevent duplicates
  const filteredParticipants = normalizedRemoteParticipants.filter(p => {
      // Check odaId
      if (p.odaId === currentUser?.id) return false;
      // Check if id is `sfu-${currentUser.id}`
      if (p.id === `sfu-${currentUser?.id}`) return false;
      return true;
  });

  const allParticipants = [localParticipant, ...filteredParticipants];

  if (isConnecting) {
    return (
      <Flex
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bg="gray.900"
        justifyContent="center"
        alignItems="center"
        zIndex="9999">
        <VStack spacing="4">
          <Spinner size="xl" color="blue.500" />
          <Text color="white" fontSize="lg">
            Konferansa bağlanılıyor...
          </Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      bg="gray.900"
      zIndex="9999">
      {/* Header */}
      <HStack
        px="4"
        py="2"
        bg="gray.800"
        justifyContent="space-between"
        borderBottom="1px solid"
        borderColor="gray.700">
        <HStack spacing="3">
          <Box
            w="3"
            h="3"
            borderRadius="full"
            bg={isConnected ? 'green.500' : 'red.500'}
          />
          <Text color="white" fontWeight="bold">
            {title || 'Video Konferans'}
          </Text>
          <Badge colorScheme="blue">{allParticipants.length} katılımcı</Badge>
          {isBroadcaster && (
            <Badge colorScheme="red" variant="solid">
              🔴 CANLI YAYIN
            </Badge>
          )}
          <Badge colorScheme={conferenceMode === 'sfu' ? 'purple' : 'gray'}>
            {conferenceMode === 'sfu' ? 'SFU' : 'P2P'}
          </Badge>
          <Tooltip label={`Ağ Kalitesi: ${networkQuality}`}>
            <HStack spacing="1">
              <FiWifi
                color={
                  networkQuality === 'excellent'
                    ? '#48BB78'
                    : networkQuality === 'good'
                      ? '#68D391'
                      : networkQuality === 'fair'
                        ? '#ECC94B'
                        : '#FC8181'
                }
              />
              <Text
                fontSize="xs"
                color={
                  networkQuality === 'excellent'
                    ? 'green.400'
                    : networkQuality === 'good'
                      ? 'green.300'
                      : networkQuality === 'fair'
                        ? 'yellow.400'
                        : 'red.400'
                }>
                {networkQuality === 'excellent'
                  ? 'Mükemmel'
                  : networkQuality === 'good'
                    ? 'İyi'
                    : networkQuality === 'fair'
                      ? 'Orta'
                      : 'Zayıf'}
              </Text>
            </HStack>
          </Tooltip>
        </HStack>
        <HStack spacing="2">
          <Tooltip label="Katılımcılar">
            <IconButton
              icon={<FiUsers />}
              variant={showParticipants ? 'solid' : 'ghost'}
              colorScheme={showParticipants ? 'blue' : 'whiteAlpha'}
              onClick={() => setShowParticipants(!showParticipants)}
              aria-label="Katılımcılar"
            />
          </Tooltip>
          <Tooltip label="Sohbet">
            <IconButton
              icon={<FiMessageSquare />}
              variant={showChat ? 'solid' : 'ghost'}
              colorScheme={showChat ? 'blue' : 'whiteAlpha'}
              onClick={() => setShowChat(!showChat)}
              aria-label="Sohbet"
            />
          </Tooltip>
        </HStack>
      </HStack>

      {/* Main content */}
      <Flex h="calc(100vh - 120px)">
        {/* Video grid */}
        <Box flex="1" p="4" overflowY="auto" position="relative">
          {/* Floating Reactions */}
          {floatingReactions.map(reaction => (
            <Box
              key={reaction.id}
              position="absolute"
              bottom="20%"
              left="50%"
              transform="translateX(-50%)"
              zIndex="100"
              animation="floatUp 3s ease-out forwards"
              sx={{
                '@keyframes floatUp': {
                  '0%': {
                    opacity: 1,
                    transform: 'translateX(-50%) translateY(0) scale(1)',
                  },
                  '100%': {
                    opacity: 0,
                    transform: 'translateX(-50%) translateY(-200px) scale(1.5)',
                  },
                },
              }}>
              <VStack spacing="1">
                <Text fontSize="4xl">{reaction.emoji}</Text>
                <Badge
                  bg="blackAlpha.700"
                  color="white"
                  borderRadius="full"
                  px="2">
                  {reaction.userName}
                </Badge>
              </VStack>
            </Box>
          ))}
          {viewMode === 'grid' ? (
            <Grid
              templateColumns={`repeat(${Math.ceil(
                Math.sqrt(allParticipants.length),
              )}, 1fr)`}
              gap="4"
              h="100%"
              autoRows="minmax(200px, 1fr)">
              {allParticipants.map((participant, idx) => (
                <GridItem key={participant.odaId || idx} role="group">
                  <VideoParticipant
                    participant={participant}
                    isLocal={participant.odaId === currentUser?.id}
                    isSpeaking={participant.odaId === activeSpeakerId}
                    isFullscreen={fullscreenUser === participant.odaId}
                    onFullscreen={setFullscreenUser}
                    isSpotlighted={spotlightUserId === participant.odaId}
                    onSpotlight={toggleSpotlight}
                  />
                </GridItem>
              ))}
            </Grid>
          ) : (
            <Flex h="100%" direction="column" gap="4">
              <Box flex="1" bg="gray.900" borderRadius="xl" overflow="hidden">
                {(() => {
                  const mainParticipant = spotlightUserId
                    ? allParticipants.find(p => p.odaId === spotlightUserId)
                    : allParticipants.find(p => p.odaId !== currentUser?.id) ||
                      localParticipant;

                  if (!mainParticipant) return null;

                  return (
                    <VideoParticipant
                      participant={mainParticipant}
                      isLocal={mainParticipant.odaId === currentUser?.id}
                      isSpeaking={mainParticipant.odaId === activeSpeakerId}
                      isFullscreen={fullscreenUser === mainParticipant.odaId}
                      onFullscreen={setFullscreenUser}
                      isSpotlighted={spotlightUserId === mainParticipant.odaId}
                      onSpotlight={toggleSpotlight}
                    />
                  );
                })()}
              </Box>
              <HStack h="150px" spacing="4" overflowX="auto" pb="2">
                {allParticipants
                  .filter(
                    p =>
                      p.odaId !==
                      (spotlightUserId ||
                        allParticipants.find(ap => ap.odaId !== currentUser?.id)
                          ?.odaId ||
                        currentUser?.id),
                  )
                  .map((participant, idx) => (
                    <Box key={participant.odaId || idx} minW="200px" h="100%">
                      <VideoParticipant
                        participant={participant}
                        isLocal={participant.odaId === currentUser?.id}
                        isSpeaking={participant.odaId === activeSpeakerId}
                        isFullscreen={fullscreenUser === participant.odaId}
                        onFullscreen={setFullscreenUser}
                        isSpotlighted={spotlightUserId === participant.odaId}
                        onSpotlight={toggleSpotlight}
                      />
                    </Box>
                  ))}
              </HStack>
            </Flex>
          )}
        </Box>

        {/* Side panels */}
        {(showChat || showParticipants) && (
          <Box
            w="350px"
            bg="gray.800"
            borderLeft="1px solid"
            borderColor="gray.700">
            {showChat && (
              <ChatPanel
                messages={messages}
                onSend={sendMessage}
                onSendFile={sendFile}
                currentUserId={currentUser?.id}
                typingUsers={typingUsers}
                replyingTo={replyingTo}
                onReply={setReplyingTo}
                onCancelReply={() => setReplyingTo(null)}
                onStartTyping={startTyping}
                onStopTyping={stopTyping}
                isUploading={isUploading}
                uploadProgress={uploadProgress}
              />
            )}
            {showParticipants && !showChat && (
              <VStack p="4" align="stretch" spacing="2">
                <Text color="white" fontWeight="bold" mb="2">
                  Katılımcılar
                </Text>
                {allParticipants.map((p, idx) => (
                  <HStack key={idx} p="2" bg="gray.700" borderRadius="md">
                    <Avatar size="sm" name={p.userName} src={p.userAvatar} />
                    <Text color="white" flex="1">
                      {p.userName}
                    </Text>
                    {!p.audioEnabled && <FiMicOff color="#EF4444" />}
                    {!p.videoEnabled && <FiVideoOff color="#EF4444" />}
                    {p.handRaised && <span>✋</span>}
                  </HStack>
                ))}
              </VStack>
            )}
            {false && (
              <VStack
                p="4"
                align="stretch"
                spacing="3"
                h="100%"
                overflowY="auto">
                <HStack justify="space-between">
                  <Text color="white" fontWeight="bold">
                    📊 Anketler
                  </Text>
                  <Button
                    size="sm"
                    colorScheme="purple"
                    leftIcon={<span>+</span>}
                    onClick={() => setShowCreatePoll(true)}>
                    Oluştur
                  </Button>
                </HStack>

                {/* Create Poll Form */}
                {showCreatePoll && (
                  <Box bg="gray.700" p="3" borderRadius="md">
                    <VStack spacing="2" align="stretch">
                      <Input
                        placeholder="Soru..."
                        value={newPollQuestion}
                        onChange={e => setNewPollQuestion(e.target.value)}
                        bg="gray.600"
                        color="white"
                        _placeholder={{color: 'gray.400'}}
                      />
                      {newPollOptions.map((opt, idx) => (
                        <HStack key={idx}>
                          <Input
                            placeholder={`Seçenek ${idx + 1}`}
                            value={opt}
                            onChange={e => {
                              const newOpts = [...newPollOptions];
                              newOpts[idx] = e.target.value;
                              setNewPollOptions(newOpts);
                            }}
                            bg="gray.600"
                            color="white"
                            _placeholder={{color: 'gray.400'}}
                          />
                          {newPollOptions.length > 2 && (
                            <IconButton
                              icon={<FiX />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() =>
                                setNewPollOptions(
                                  newPollOptions.filter((_, i) => i !== idx),
                                )
                              }
                            />
                          )}
                        </HStack>
                      ))}
                      {newPollOptions.length < 6 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          colorScheme="gray"
                          onClick={() =>
                            setNewPollOptions([...newPollOptions, ''])
                          }>
                          + Seçenek Ekle
                        </Button>
                      )}
                      <HStack>
                        <Button
                          size="sm"
                          colorScheme="purple"
                          onClick={createPoll}>
                          Anketi Gönder
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowCreatePoll(false)}>
                          İptal
                        </Button>
                      </HStack>
                    </VStack>
                  </Box>
                )}

                {/* Poll List */}
                {polls.length === 0 ? (
                  <Text color="gray.400" textAlign="center" py="4">
                    Henüz anket yok
                  </Text>
                ) : (
                  polls.map(poll => {
                    const hasVoted = poll.votes?.some(
                      v => v.odaId === currentUser?.id,
                    );
                    const myVote = poll.votes?.find(
                      v => v.odaId === currentUser?.id,
                    );
                    const totalVotes =
                      poll.totalVotes || poll.votes?.length || 0;

                    return (
                      <Box
                        key={poll.id}
                        bg={poll.isActive ? 'purple.900' : 'gray.700'}
                        p="3"
                        borderRadius="md"
                        border="1px solid"
                        borderColor={poll.isActive ? 'purple.500' : 'gray.600'}>
                        <HStack justify="space-between" mb="2">
                          <Text color="white" fontWeight="600" fontSize="sm">
                            {poll.question}
                          </Text>
                          <Badge colorScheme={poll.isActive ? 'green' : 'gray'}>
                            {poll.isActive ? 'Aktif' : 'Kapandı'}
                          </Badge>
                        </HStack>

                        <VStack spacing="1" align="stretch">
                          {poll.options.map((option, idx) => {
                            const percentage =
                              totalVotes > 0
                                ? Math.round(
                                    (option.voteCount / totalVotes) * 100,
                                  )
                                : 0;
                            const isSelected = myVote?.optionIndex === idx;

                            return (
                              <Box
                                key={idx}
                                position="relative"
                                bg={isSelected ? 'purple.600' : 'gray.600'}
                                p="3"
                                borderRadius="md"
                                cursor={
                                  poll.isActive && !hasVoted
                                    ? 'pointer'
                                    : 'default'
                                }
                                onClick={() =>
                                  poll.isActive &&
                                  !hasVoted &&
                                  votePoll(poll.id, idx)
                                }
                                _hover={
                                  poll.isActive && !hasVoted
                                    ? {bg: 'purple.700'}
                                    : {}
                                }
                                overflow="hidden">
                                {(hasVoted || !poll.isActive) && (
                                  <Box
                                    position="absolute"
                                    left="0"
                                    top="0"
                                    bottom="0"
                                    width={`${percentage}%`}
                                    bg={
                                      isSelected ? 'purple.400' : 'purple.500'
                                    }
                                    opacity="0.4"
                                    transition="width 0.5s ease"
                                  />
                                )}
                                <VStack
                                  position="relative"
                                  align="stretch"
                                  spacing="1">
                                  <HStack justify="space-between">
                                    <HStack spacing="2">
                                      {isSelected && (
                                        <span style={{color: '#4ade80'}}>
                                          ✓
                                        </span>
                                      )}
                                      <Text
                                        color="white"
                                        fontSize="sm"
                                        fontWeight={isSelected ? '600' : '400'}>
                                        {option.text}
                                      </Text>
                                    </HStack>
                                    {(hasVoted || !poll.isActive) && (
                                      <Badge
                                        colorScheme="purple"
                                        fontSize="sm"
                                        fontWeight="bold"
                                        px="2">
                                        {percentage}%
                                      </Badge>
                                    )}
                                  </HStack>
                                  {(hasVoted || !poll.isActive) && (
                                    <Text color="gray.300" fontSize="xs">
                                      {option.voteCount || 0} oy
                                    </Text>
                                  )}
                                </VStack>
                              </Box>
                            );
                          })}
                        </VStack>

                        <HStack mt="2" justify="space-between">
                          <Text color="gray.400" fontSize="xs">
                            {totalVotes} oy
                          </Text>
                          {poll.isActive &&
                            poll.createdBy?.odaId === currentUser?.id && (
                              <Button
                                size="xs"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => closePoll(poll.id)}>
                                Kapat
                              </Button>
                            )}
                        </HStack>
                      </Box>
                    );
                  })
                )}
              </VStack>
            )}
          </Box>
        )}
      </Flex>

      {/* Controls */}
      <HStack
        position="absolute"
        bottom="0"
        left="0"
        right="0"
        py="4"
        justifyContent="center"
        bg="gray.800"
        borderTop="1px solid"
        borderColor="gray.700"
        spacing="4">
        <Tooltip label={audioEnabled ? 'Mikrofonu Kapat' : 'Mikrofonu Aç'}>
          <IconButton
            icon={audioEnabled ? <FiMic /> : <FiMicOff />}
            size="lg"
            borderRadius="full"
            colorScheme={audioEnabled ? 'gray' : 'red'}
            onClick={toggleAudio}
            aria-label="Mikrofon"
          />
        </Tooltip>

        <Tooltip label={videoEnabled ? 'Kamerayı Kapat' : 'Kamerayı Aç'}>
          <IconButton
            icon={videoEnabled ? <FiVideo /> : <FiVideoOff />}
            size="lg"
            borderRadius="full"
            colorScheme={videoEnabled ? 'gray' : 'red'}
            onClick={toggleVideo}
            aria-label="Kamera"
          />
        </Tooltip>

        <Tooltip label={handRaised ? 'El İndir' : 'El Kaldır'}>
          <IconButton
            icon={<span>✋</span>}
            size="lg"
            borderRadius="full"
            colorScheme={handRaised ? 'yellow' : 'gray'}
            onClick={toggleHandRaise}
            aria-label="El Kaldır"
          />
        </Tooltip>

        <Tooltip label={isScreenSharing ? 'Paylaşımı Durdur' : 'Ekran Paylaş'}>
          <IconButton
            icon={isScreenSharing ? <FiStopCircle /> : <FiMonitor />}
            size="lg"
            borderRadius="full"
            colorScheme={isScreenSharing ? 'red' : 'gray'}
            onClick={isScreenSharing ? stopScreenShare : startScreenShare}
            aria-label="Ekran Paylaşımı"
          />
        </Tooltip>

        <Tooltip label={isRecording ? 'Kaydı Durdur' : 'Kaydı Başlat'}>
          <HStack spacing={0}>
            <IconButton
              icon={<FiDisc />}
              size="lg"
              borderRadius={isRecording ? 'full' : 'full'}
              borderTopRightRadius={isRecording ? 0 : 'full'}
              borderBottomRightRadius={isRecording ? 0 : 'full'}
              colorScheme={isRecording ? 'red' : 'gray'}
              onClick={toggleRecording}
              aria-label="Kayıt"
              className={isRecording ? 'pulse-animation' : ''}
            />
            {isRecording && (
              <Box
                bg="red.600"
                h="48px"
                display="flex"
                alignItems="center"
                px={3}
                borderTopRightRadius="full"
                borderBottomRightRadius="full">
                <Text color="white" fontWeight="bold">
                  {formatDuration(recordingDuration)}
                </Text>
              </Box>
            )}
          </HStack>
        </Tooltip>

        <Menu placement="top">
          <Tooltip label="Görünüm">
            <MenuButton
              as={IconButton}
              icon={viewMode === 'grid' ? <FiGrid /> : <FiLayout />}
              size="lg"
              borderRadius="full"
              colorScheme="gray"
              aria-label="Görünüm Modu"
            />
          </Tooltip>
          <MenuList bg="gray.800" borderColor="gray.700">
            <MenuItem
              bg="gray.800"
              _hover={{bg: 'gray.700'}}
              color="white"
              icon={<FiGrid />}
              onClick={() => setViewMode('grid')}>
              Izgara Görünümü
            </MenuItem>
            <MenuItem
              bg="gray.800"
              _hover={{bg: 'gray.700'}}
              color="white"
              icon={<FiLayout />}
              onClick={() => setViewMode('speaker')}>
              Konuşmacı Görünümü
            </MenuItem>
          </MenuList>
        </Menu>

        <Box position="relative">
          <Tooltip label="Tepki Gönder">
            <IconButton
              icon={<span>😀</span>}
              size="lg"
              borderRadius="full"
              colorScheme={showReactionPicker ? 'blue' : 'gray'}
              onClick={() => setShowReactionPicker(!showReactionPicker)}
              aria-label="Tepki"
            />
          </Tooltip>

          {/* Reaction Picker */}
          {showReactionPicker && (
            <HStack
              position="absolute"
              bottom="60px"
              left="50%"
              transform="translateX(-50%)"
              bg="gray.800"
              p="3"
              borderRadius="full"
              boxShadow="dark-lg"
              spacing="2"
              zIndex="1000">
              {['👍', '❤️', '😂', '👏', '🎉', '🔥'].map(emoji => (
                <Box
                  key={emoji}
                  cursor="pointer"
                  fontSize="2xl"
                  onClick={() => sendReaction(emoji)}
                  _hover={{transform: 'scale(1.3)'}}
                  transition="transform 0.1s">
                  {emoji}
                </Box>
              ))}
            </HStack>
          )}
        </Box>

        <Tooltip label="Konferanstan Ayrıl">
          <IconButton
            icon={<FiPhoneOff />}
            size="lg"
            borderRadius="full"
            colorScheme="red"
            onClick={leaveConference}
            aria-label="Ayrıl"
          />
        </Tooltip>
      </HStack>
    </Box>
  );
};

export default VideoConference;
